import type {ReactElement} from 'react';
import {useMemo} from 'react';
import {EventWizardOptionTile} from '@Components/event-wizard/event-wizard-option-tile';
import {
  useEventWizardIsPaidTicketingFeatureAvailable,
  useEventWizardIsRecurring,
  useEventWizardIsTicketingCountrySupported,
  useEventWizardRegistrationDetails,
  useEventWizardSelectedRegistrationMode,
  useEventWizardTicketingConfig,
} from '@Components/event-wizard/event-wizard.hooks';
import {onEventWizardRegistrationModeSelect, onEventWizardTicketingButtonTextChange} from '@Components/event-wizard/event-wizard-slice';
import {TicketingTicketDetails} from '@Components/event-wizard/event-wizard-ticketing/components/ticketing-ticket-details/ticketing-ticket-details';
import {TicketingCustomForm} from '@Components/event-wizard/event-wizard-ticketing/components/ticketing-custom-form/ticketing-custom-form';
import {TicketingCustomLinkCard} from '@Components/event-wizard/event-wizard-ticketing/components/ticketing-custom-link-card/ticketing-custom-link-card';
import {getRegistrationModeTiles, RegistrationMode} from '@Components/event-wizard/event-wizard.types';
import {useAppDispatch} from '@/hooks';
import styles from './event-wizard-ticketing.module.scss';
import {getButtonTextOptions, getButtonTextOptionsForMode} from '@Libraries/events.library';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {requireTicketingSelection} from '@Components/event-wizard/event-wizard-ticketing/ticketing-gating.helper';
import {MAX_BUTTON_TEXT_LENGTH} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {getStoredButtonTextForMode, REGISTRATION_MODE_TO_GA4_PARAM} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.helper';
import {ButtonTextPicker} from '@Components/event-wizard/event-wizard-ticketing/components/button-text-picker/button-text-picker';
import TooltipTrigger from '@Components/tooltip-trigger';
import {TooltipTriggerEvent} from '@Components/tooltip-trigger/tooltip-trigger';
import {TOOLTIP_POSITION} from '@Components/tooltip';
import {isMobile as isTouchDevice} from 'react-device-detect';

/**
 * Only Tickets is unavailable on a recurring event. Recurring RSVP is fully supported — its Hi.Events event
 * is provisioned on first registration instead of at save.
 */
const isUnavailableOnRecurring = (mode: RegistrationMode): boolean => {
  return mode === RegistrationMode.TICKETS;
};

export function EventWizardTicketing(): ReactElement {
  const dispatch = useAppDispatch();
  const registrationDetails = useEventWizardRegistrationDetails();
  const selectedMode = useEventWizardSelectedRegistrationMode();
  const registrationModeTiles = useMemo(() => getRegistrationModeTiles(), []);
  const config = useEventWizardTicketingConfig();
  const isRecurring = useEventWizardIsRecurring();
  const isTicketingCountrySupported = useEventWizardIsTicketingCountrySupported();
  const isPaidTicketingFeatureAvailable = useEventWizardIsPaidTicketingFeatureAvailable();

  const selectMode = (mode: RegistrationMode): void => {
    if (mode === selectedMode) {
      return;
    }
    // RSVP is never gated here, and neither dialog the helper may open selects the mode.
    if (mode === RegistrationMode.TICKETS && !requireTicketingSelection(isPaidTicketingFeatureAvailable, isTicketingCountrySupported)) {
      return;
    }
    dispatch(onEventWizardRegistrationModeSelect(mode));
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_TICKETING_TYPE_SWITCH, {[GA4EventParamName.TO]: REGISTRATION_MODE_TO_GA4_PARAM[mode]});
  };

  const getTicketDetailsSection = (): ReactElement | null => {
    if (selectedMode !== RegistrationMode.TICKETS) {
      return null;
    }
    return (
      <>
        <TicketingTicketDetails />
        <div className="border-s-standard _no-border-left _no-border-right _no-border-bottom _full-width spacing-m-t-4 spacing-m-b-4" />
      </>
    );
  };

  const getRegistrationConfigForm = (): ReactElement => {
    return (
      <>
        <div className="flex-column-align-start _full-width spacing-m-t-4 spacing-p-t-4">
          {getTicketDetailsSection()}
          <div className="_full-width">
            <TicketingCustomForm />
          </div>
          <div className="spacing-m-t-4 _full-width">
            <ButtonTextPicker
              value={getStoredButtonTextForMode(selectedMode, config, registrationDetails)}
              onChange={(text: string): void => {
                dispatch(onEventWizardTicketingButtonTextChange(text));
              }}
              options={getButtonTextOptions(getButtonTextOptionsForMode(selectedMode), registrationDetails.customBtnTexts ?? [])}
              label={window.i18next.t('pmwjs_button_text')}
              maxLength={MAX_BUTTON_TEXT_LENGTH}
            />
          </div>
        </div>
      </>
    );
  };

  const getSelectedConfig = (): ReactElement | null => {
    if (selectedMode === RegistrationMode.TICKETS || selectedMode === RegistrationMode.RSVP) {
      return getRegistrationConfigForm();
    }
    if (selectedMode === RegistrationMode.CUSTOM_LINK) {
      return <TicketingCustomLinkCard />;
    }
    return null;
  };

  /**
   * A disabled tile swallows its own click, so the reason it is unavailable has to be reachable some other way.
   * Only the disabled tile is wrapped, so every other tile stays a direct child of the grid.
   */
  const getTileWithUnavailableReason = (tile: ReactElement, mode: RegistrationMode, isDisabled: boolean): ReactElement => {
    if (!isDisabled) {
      return tile;
    }

    return (
      <TooltipTrigger
        key={mode}
        text={window.i18next.t('pmwjs_recurring_ticketing_unavailable')}
        triggerEvent={isTouchDevice ? TooltipTriggerEvent.CLICK : TooltipTriggerEvent.HOVER}
        position={TOOLTIP_POSITION.TOP_RIGHT}
        overlayClass={styles.tileTooltipTrigger}
        tooltipClass={`${styles.tileTooltip} _pointer-events-none`}
      >
        {tile}
      </TooltipTrigger>
    );
  };

  const getTiles = (): ReactElement[] => {
    return registrationModeTiles.map((descriptor) => {
      const isDisabled = isRecurring && isUnavailableOnRecurring(descriptor.mode);

      return getTileWithUnavailableReason(
        <EventWizardOptionTile
          key={descriptor.mode}
          icon={descriptor.icon}
          title={descriptor.title}
          description={descriptor.description}
          isSelected={selectedMode === descriptor.mode}
          isDisabled={isDisabled}
          isNew={descriptor.isNew}
          onClick={(): void => {
            selectMode(descriptor.mode);
          }}
        />,
        descriptor.mode,
        isDisabled
      );
    });
  };

  return (
    <div className="flex-column-align-start _full-width">
      <h4 className="heading-h4 spacing-p-0 ct-neutral-primary spacing-m-b-4">{window.i18next.t('pmwjs_event_wizard_tickets_and_registration')}</h4>
      <div className={`${styles.tileGrid} _full-width`}>{getTiles()}</div>
      {getSelectedConfig()}
    </div>
  );
}
